import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form/form';
import { Input } from '@/components/ui/input';

import { useCreateSupplier } from '../api/supplier-mutations';

const addManagedSupplierSchema = z.object({
  title: z.string().min(1, 'Supplier name is required'),
});

type AddManagedSupplierFormData = z.infer<typeof addManagedSupplierSchema>;

interface AddManagedSupplierDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddManagedSupplierDialog({
  open,
  onOpenChange,
}: AddManagedSupplierDialogProps) {
  const createManagedSupplier = useCreateSupplier();

  const form = useForm<AddManagedSupplierFormData>({
    resolver: zodResolver(addManagedSupplierSchema),
    defaultValues: {
      title: '',
    },
  });

  const onSubmit = async (data: AddManagedSupplierFormData) => {
    try {
      await createManagedSupplier.mutateAsync([
        {
          title: data.title,
          organisation_id: null, // null for managed suppliers
        },
      ]);
      form.reset();
      onOpenChange(false);
    } catch {
      toast.error('Failed to create supplier. Please try again.');
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add Managed Supplier</DialogTitle>
          <DialogDescription>
            Create a new managed supplier that will be available to all
            organisations.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Supplier Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter supplier name"
                      {...field}
                      disabled={createManagedSupplier.isPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={createManagedSupplier.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createManagedSupplier.isPending}>
                {createManagedSupplier.isPending
                  ? 'Creating...'
                  : 'Create Supplier'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
